import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, X, Send, Clock3 } from "lucide-react";

/**
 * Global banner that shows pending (scheduled) emails with a live countdown.
 * Refreshes every 30s. User can cancel or send-now per item.
 */
export default function EmailPendingBanner() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState({});
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/emails/pending");
      setItems(data.items || []);
    } catch { /* silently ignore */ }
  };

  useEffect(() => {
    load();
    const poll = setInterval(load, 30_000);
    // force re-render every second for countdowns
    timerRef.current = setInterval(() => setItems((items) => [...items]), 1000);
    return () => { clearInterval(poll); clearInterval(timerRef.current); };
  }, []);

  const cancel = async (qid) => {
    setBusy((b) => ({ ...b, [qid]: "cancel" }));
    try {
      await api.post(`/emails/cancel/${qid}`);
      toast.success("Email annulé");
      setItems((its) => its.filter((i) => i.queue_id !== qid));
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy((b) => ({ ...b, [qid]: null })); }
  };

  const sendNow = async (qid) => {
    setBusy((b) => ({ ...b, [qid]: "send" }));
    try {
      const { data } = await api.post(`/emails/send-now/${qid}`);
      if (data.ok) {
        toast.success("Email envoyé");
        setItems((its) => its.filter((i) => i.queue_id !== qid));
      } else {
        toast.error(data.error || "Envoi échoué");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy((b) => ({ ...b, [qid]: null })); }
  };

  if (items.length === 0) return null;

  return (
    <div className="fixed bottom-20 right-4 left-4 sm:left-auto sm:right-4 sm:max-w-sm z-40 space-y-2" data-testid="email-pending-banner">
      {items.slice(0, 3).map((i) => {
        const ms = new Date(i.send_at).getTime() - Date.now();
        const s = Math.max(0, Math.round(ms / 1000));
        const mm = String(Math.floor(s / 60)).padStart(2, "0");
        const ss = String(s % 60).padStart(2, "0");
        return (
          <div key={i.queue_id} className="bg-black text-white rounded-2xl p-4 shadow-2xl border border-white/10" data-testid={`email-pending-${i.queue_id}`}>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <div className="text-xs font-bold uppercase tracking-widest text-white/70">Email prêt</div>
              <div className="ml-auto flex items-center gap-1 text-xs text-white/80">
                <Clock3 className="w-3.5 h-3.5" />
                <span className="font-mono">{mm}:{ss}</span>
              </div>
            </div>
            <div className="mt-2 font-display font-bold text-sm leading-tight truncate">{i.subject}</div>
            <div className="text-xs text-white/70 truncate">À {i.to_name || i.to_email}</div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => sendNow(i.queue_id)}
                disabled={!!busy[i.queue_id]}
                className="flex-1 rounded-full bg-[var(--surface)] text-[var(--text)] h-9 text-xs font-semibold hover:bg-white/90 inline-flex items-center justify-center gap-1.5"
                data-testid={`email-send-now-${i.queue_id}`}
              >
                <Send className="w-3.5 h-3.5" /> {busy[i.queue_id] === "send" ? "…" : "Envoyer maintenant"}
              </button>
              <button
                onClick={() => cancel(i.queue_id)}
                disabled={!!busy[i.queue_id]}
                className="rounded-full border border-white/25 h-9 px-3 text-xs font-semibold hover:bg-white/10 inline-flex items-center gap-1.5"
                data-testid={`email-cancel-${i.queue_id}`}
              >
                <X className="w-3.5 h-3.5" /> Annuler
              </button>
            </div>
          </div>
        );
      })}
      {items.length > 3 && (
        <Link to="/emails/history" className="block text-center text-xs text-zinc-400 underline underline-offset-4">
          +{items.length - 3} autre{items.length - 3 > 1 ? "s" : ""} en file
        </Link>
      )}
    </div>
  );
}
